import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import { selectOrganizationData } from 'pages/redux';
import { selectBranchFiles, selectActivityGroups, selectActivityLinks } from 'pages/Logs/redux';
import {
  selectCustomers,
  selectUsers,
  selectBilling,
} from 'pages/Settings/redux';
import { selectManagerReports } from 'pages/Reporting/redux';
import { selectErpRecords } from 'pages/Records/redux';
import {
  countPhotoUsingItems,
  estimateBranchFilesBytes,
  estimateDbBytesFromCounts,
  estimatePhotoBytes,
  formatStorageBytes,
  hasStoragePlan,
  resolveDbStorageMaxBytes,
  resolveOrganizationStorageMaxBytes,
  resolveOthersStorageMaxBytes,
  resolveOthersStorageWarnHeadroomBytes,
  resolveStorageWarnHeadroomBytes,
} from 'pages/Logs/logUtils';

export type OrganizationStorageSnapshot = {
  dbBytes: number,
  photoBytes: number,
  branchBytes: number,
  othersBytes: number,
  totalBytes: number,
  organizationMaxBytes: number,
  dbMaxBytes: number,
  othersMaxBytes: number,
  hasPlan: boolean,
  /** DB tone for right-pane alert (all DB entries). */
  dbTone: 'warn' | 'critical' | null,
  /** Photo/file tone for right-pane alert (free tier others bucket). */
  othersTone: 'warn' | 'critical' | null,
  format: typeof formatStorageBytes,
};

/** Organization storage estimate. activity_logs bytes use DB COUNT(*) (activityLogsCount), not the local search page. */
export const useOrganizationStorage = (): OrganizationStorageSnapshot => {
  const organizationData = useAppSelector(selectOrganizationData);
  const customers = useAppSelector(selectCustomers);
  const users = useAppSelector(selectUsers);
  const cards = useAppSelector(selectBilling);
  const branchFiles = useAppSelector(selectBranchFiles);
  const managerReports = useAppSelector(selectManagerReports);
  const erpRecords = useAppSelector(selectErpRecords);
  const activityGroups = useAppSelector(selectActivityGroups);
  const activityLinks = useAppSelector(selectActivityLinks);
  const inventory = organizationData && Array.isArray(organizationData.inventory) ? organizationData.inventory : [];

  return useMemo(() => {
    const inlinePhotoCount = countPhotoUsingItems({ inventory, users });
    const photoCount = Math.max(
      Number(organizationData?.photosCount) || 0,
      inlinePhotoCount,
    );
    const feedbackCount = (customers || []).reduce((n, customer) => {
      const messages = customer?.feedback?.messages;
      return n + (Array.isArray(messages) ? messages.length : 0);
    }, 0);
    const linkedSkuBuckets = (activityGroups || []).flatMap((g: any) => g?.groupTypes || []);
    const linkedSkuItems = linkedSkuBuckets.reduce(
      (n: number, gt: any) => n + (Array.isArray(gt?.skuIds) ? gt.skuIds.length : 0),
      0,
    );
    const dbBytes = estimateDbBytesFromCounts({
      logs: Number(organizationData?.activityLogsCount) || 0,
      customers: customers?.length || 0,
      inventory: inventory.length,
      rooms: 0,
      staff: Number(organizationData?.staffSeatCount) || users?.length || 0,
      billing: cards?.length || 0,
      feedback: feedbackCount,
      config: 1,
      organizations: 1,
      photos: photoCount,
      managerReports: managerReports?.length || 0,
      activityGroups: activityGroups?.length || 0,
      activityGroupLinks: activityLinks?.length || 0,
      activityGroupLinkedSkus: linkedSkuBuckets.length,
      activityGroupLinkedSkuItems: linkedSkuItems,
      erpRecords: erpRecords?.length || 0,
      employeeInvites: (erpRecords || []).filter(
        (row) => row.kind === 'employee' && String(row.fields?.isOwner || '') !== 'Yes',
      ).length,
      // Ceiling: one kind-level grant row per catalog kind, per staff with access.
      staffRecordGrants: (users || []).length * 40,
    });
    const photoBytes = estimatePhotoBytes(photoCount);
    const branchBytes = estimateBranchFilesBytes(branchFiles);
    const othersBytes = branchBytes + photoBytes;
    const totalBytes = dbBytes + othersBytes;
    const hasPlan = hasStoragePlan(organizationData);
    const organizationMaxBytes = resolveOrganizationStorageMaxBytes(organizationData);
    const dbMaxBytes = resolveDbStorageMaxBytes(organizationData);
    const othersMaxBytes = resolveOthersStorageMaxBytes(organizationData);
    const warnHeadroom = resolveStorageWarnHeadroomBytes(organizationData);
    const othersWarnHeadroom = resolveOthersStorageWarnHeadroomBytes(organizationData);

    let dbTone: 'warn' | 'critical' | null = null;
    if (hasPlan) {
      if (totalBytes >= organizationMaxBytes) dbTone = 'critical';
      else if (organizationMaxBytes - totalBytes < warnHeadroom) dbTone = 'warn';
    } else {
      if (dbBytes >= dbMaxBytes) dbTone = 'critical';
      else if (dbMaxBytes - dbBytes < warnHeadroom) dbTone = 'warn';
    }

    let othersTone: 'warn' | 'critical' | null = null;
    if (!hasPlan) {
      if (othersBytes >= othersMaxBytes) othersTone = 'critical';
      else if (othersMaxBytes - othersBytes < othersWarnHeadroom) othersTone = 'warn';
    }

    return {
      dbBytes,
      photoBytes,
      branchBytes,
      othersBytes,
      totalBytes,
      organizationMaxBytes,
      dbMaxBytes,
      othersMaxBytes,
      hasPlan,
      dbTone,
      othersTone,
      format: formatStorageBytes,
    };
  }, [
    organizationData,
    customers,
    users,
    cards,
    branchFiles,
    managerReports,
    activityGroups,
    activityLinks,
    erpRecords,
  ]);
};

export default useOrganizationStorage;
